import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import cn from 'classnames';

import Loadable from '@components/Loadable/Loadable';
import PrintButton from '@components/PrintButton/PrintButton';
import { TPrintButtonAction } from '@components/PrintButton/PrintButton.interface';
import RiskLevelsLegend from '@components/RiskLevelsLegend/RiskLevelsLegend';
import { InquiryEntryType } from '@interfaces/inquiries';
import { adminRiskLevelsIsReadySelector } from '@store/features/adminRiskLevels/selectors';
import formatDate from '@utils/formatDate';

import PrintResponseCard from './PrintResponseCard/PrintResponseCard';
import PrintResponseMedicationCard from './PrintResponseMedicationCard/PrintResponseMedicationCard';
import { IPrintViewProps } from './PrintView.interface';

import './style.scss';

const PrintView: FC<IPrintViewProps> = ({
  className,
  data,
  isLoading,
  error,
  inquiryType,
  getResponse,
}) => {
  const { t } = useTranslation();

  const isRiskLevelReady = useSelector(adminRiskLevelsIsReadySelector);

  useEffect(() => {
    if (!!data && !isLoading) {
      if (inquiryType === InquiryEntryType.Supplement && !isRiskLevelReady) {
        return;
      }
      window.parent.postMessage({ action: TPrintButtonAction.PrintPageLoaded });
    }
  }, [data, isLoading, isRiskLevelReady, inquiryType]);

  return (
    <div className={cn('print-view__content', className)}>
      <div className="print-view__main">
        <div className="print-view__header">
          <Typography variant="h2" className="print-view__supplements-title">
            {inquiryType === InquiryEntryType.Supplement && t('inquiryResponse.supplementsTitle')}
            {inquiryType === InquiryEntryType.Medication && t('inquiryResponse.medicationsTitle')}
          </Typography>

          <PrintButton
            buttonText={t('common.printResponses')}
            disableIframe
            onClick={window?.print}
          />
        </div>

        <Loadable
          isLoading={!data && isLoading}
          error={error && t('errors.inquiryResponseError')}
          onRetry={getResponse}
        >
          {!!data && (
            <>
              <div className="print-view__inquiry">
                <div className="print-view__inquiry-meta">
                  <div className="print-view__inquiry-name">{data.name}</div>
                  <div className="print-view__inquiry-date">
                    {t('inquiryResponse.submitted')} {formatDate(data.submittedDate)}
                  </div>
                </div>
              </div>

              <div className="print-view__supplements-list">
                {inquiryType === InquiryEntryType.Supplement &&
                  data.inquiryResponseDetailsDtos.map(item => (
                    <PrintResponseCard
                      key={item.id}
                      className="print-view__supplement-item"
                      item={item}
                      programCode={data?.programCodeId}
                    />
                  ))}
                {inquiryType === InquiryEntryType.Medication &&
                  data.medicationInquiryResponseDetailsDtos.map(item => (
                    <PrintResponseMedicationCard
                      key={item.userEnteredMedicationId}
                      className="print-view__supplement-item"
                      item={item}
                    />
                  ))}
              </div>
            </>
          )}

          {inquiryType === InquiryEntryType.Supplement && (
            <RiskLevelsLegend programCode={data?.programCodeId} />
          )}
        </Loadable>
      </div>
    </div>
  );
};

export default PrintView;
